import { Route } from '@angular/router';
import {
  authGuard,
  deniedPageGuard,
  provideAuthRoutes,
  requireAnyPermission,
  requirePermission,
  AcceptInvitationComponent,
  InvitationEditComponent,
  AuthSettingsComponent,
} from '@zag/auth/ui';

import { ControlLayoutComponent } from './components/control-layout.component';
import { MinimalLayoutComponent } from './minimal-layout.component';
import { controlAuthGuard } from './guards/control-auth.guard';
import { noEnvAccessGuard } from './guards/no-env-access.guard';

export const appRoutes: Route[] = [
  // Root redirect — goes to environment context (resolved by layout)
  {
    path: '',
    pathMatch: 'full',
    redirectTo: '/select-environment',
  },

  // Accept invitation — public route, no authentication required.
  // MUST be placed before any guarded route that matches 'auth/*'.
  {
    path: 'auth/accept-invitation',
    component: AcceptInvitationComponent,
    title: 'Accept Invitation - SmartCapacity Control',
  },

  // Auth routes (login only — no self-registration in the control UI)
  ...provideAuthRoutes({
    loginTitle: 'Login - SmartCapacity Control',
    showRegister: false,
  }),

  {
    path: 'auth',
    component: MinimalLayoutComponent,
    canActivate: [authGuard],
    children: [
      {
        path: 'pending',
        loadComponent: () => import('./routes/pending-access.component').then(m => m.PendingAccessComponent),
        title: 'Access Pending - SmartCapacity Control',
      },
      {
        path: 'error',
        loadComponent: () => import('./routes/permission-error.component').then(m => m.PermissionErrorComponent),
        title: 'Permission Error - SmartCapacity Control',
      },
      {
        path: 'no-env-access',
        loadComponent: () => import('./routes/no-env-access.component').then(m => m.NoEnvAccessComponent),
        title: 'No Environment Access - SmartCapacity Control',
      },
    ],
  },

  // Authenticated routes with control layout
  {
    path: '',
    component: ControlLayoutComponent,
    canActivate: [controlAuthGuard],
    canActivateChild: [controlAuthGuard],
    children: [
      {
        path: 'auth/denied',
        canActivate: [deniedPageGuard],
        loadComponent: () => import('./routes/denied-access.component').then(m => m.DeniedAccessComponent),
        title: 'Access Denied - SmartCapacity Control',
      },

      // Profile management page
      {
        path: 'profile',
        loadComponent: () => import('./routes/profile.component').then(m => m.CtrlProfileComponent),
        title: 'My Profile - SmartCapacity Control',
        data: {
          breadcrumb: { label: 'navigation.profile', icon: 'person' },
        },
      },

      // Environment selection landing page (when no env is active yet)
      {
        path: 'select-environment',
        canActivate: [requireAnyPermission('environments:read', 'environments:create'), noEnvAccessGuard],
        loadComponent: () => import('./routes/select-environment.component').then(m => m.SelectEnvironmentComponent),
        title: 'Select Environment - SmartCapacity Control',
      },

      // ─── Environment-scoped routes ──────────────────────────────────
      {
        path: 'environments/:slug',
        canActivateChild: [noEnvAccessGuard],
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'tenants',
          },
          {
            path: 'dashboard',
            canActivate: [requirePermission('environments:read', 'tenants:read')],
            loadComponent: () => import('./routes/env-dashboard.component').then(m => m.EnvDashboardComponent),
            title: 'Dashboard - SmartCapacity Control',
          },
          {
            path: 'tenants',
            children: [
              {
                path: '',
                canActivate: [requirePermission('environments:read', 'tenants:read')],
                loadComponent: () => import('./routes/tenant-list.component').then(m => m.TenantListComponent),
                title: 'Tenants - SmartCapacity Control',
              },
              {
                path: 'onboarding/:runId',
                canActivate: [requirePermission('environments:read', 'tenants:create')],
                loadComponent: () =>
                  import('./routes/tenant-onboarding/tenant-onboarding.component').then(m => m.TenantOnboardingComponent),
                title: 'New Tenant - SmartCapacity Control',
              },
              {
                path: ':subdomain/reseed/:runId',
                canActivate: [requirePermission('environments:read', 'tenants:provision')],
                loadComponent: () => import('./routes/tenant-reseed/tenant-reseed.component').then(m => m.TenantReseedComponent),
                title: 'Reseed Tenant - SmartCapacity Control',
              },
              {
                path: ':subdomain',
                canActivate: [requirePermission('environments:read', 'tenants:read')],
                loadComponent: () => import('./routes/tenant-detail.component').then(m => m.TenantDetailComponent),
                title: 'Tenant Detail - SmartCapacity Control',
                data: {
                  breadcrumb: {
                    label: ':subdomain',
                    resolver: 'tenant',
                    siblingsLoader: 'tenants',
                  },
                },
              },
            ],
          },
          {
            path: 'settings',
            canActivate: [requireAnyPermission('environments:update', 'environments:grants:read', 'environments:grants:update')],
            loadComponent: () => import('./routes/env-settings.component').then(m => m.EnvSettingsComponent),
            title: 'Environment Settings - SmartCapacity Control',
          },
        ],
      },

      // ─── Cross-environment admin routes ─────────────────────────────
      {
        path: 'admin',
        canActivate: [
          requireAnyPermission(
            'rbac:users:read',
            'rbac:users:update',
            'rbac:roles:read',
            'rbac:roles:update',
            'rbac:groups:read',
            'rbac:groups:update',
            'rbac:permissions:read',
            'settings:auth:read',
            'settings:smtp:read',
            'settings:ai:read'
          ),
        ],
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'users',
          },
          {
            path: 'users',
            canActivate: [requireAnyPermission('rbac:users:read', 'rbac:users:update')],
            loadComponent: () => import('./routes/admin/users-list.component').then(m => m.AdminUsersListComponent),
            title: 'Users - SmartCapacity Control',
          },
          {
            path: 'users/invitation/:id',
            canActivate: [requireAnyPermission('rbac:users:read', 'rbac:users:update')],
            component: InvitationEditComponent,
            title: 'Edit Invitation - SmartCapacity Control',
            data: {
              breadcrumb: {
                label: 'settings.users.actions.reconfigure',
              },
            },
          },
          {
            path: 'users/:id',
            canActivate: [requireAnyPermission('rbac:users:read', 'rbac:users:update')],
            loadComponent: () => import('./routes/admin/user-edit.component').then(m => m.AdminUserEditComponent),
            title: 'Edit User - SmartCapacity Control',
          },
          {
            path: 'roles',
            canActivate: [requireAnyPermission('rbac:roles:read', 'rbac:roles:update')],
            loadComponent: () => import('./routes/admin/roles-list.component').then(m => m.AdminRolesListComponent),
            title: 'Roles - SmartCapacity Control',
          },
          {
            path: 'roles/new',
            canActivate: [requireAnyPermission('rbac:roles:update')],
            loadComponent: () => import('./routes/admin/role-edit.component').then(m => m.AdminRoleEditComponent),
            title: 'Create Role - SmartCapacity Control',
          },
          {
            path: 'roles/:id',
            canActivate: [requireAnyPermission('rbac:roles:read', 'rbac:roles:update')],
            loadComponent: () => import('./routes/admin/role-edit.component').then(m => m.AdminRoleEditComponent),
            title: 'Edit Role - SmartCapacity Control',
          },
          {
            path: 'groups',
            canActivate: [requireAnyPermission('rbac:groups:read', 'rbac:groups:update')],
            loadComponent: () => import('./routes/admin/groups-list.component').then(m => m.AdminGroupsListComponent),
            title: 'Groups - SmartCapacity Control',
          },
          {
            path: 'groups/new',
            canActivate: [requireAnyPermission('rbac:groups:update')],
            loadComponent: () => import('./routes/admin/group-edit.component').then(m => m.AdminGroupEditComponent),
            title: 'Create Group - SmartCapacity Control',
          },
          {
            path: 'groups/:id',
            canActivate: [requireAnyPermission('rbac:groups:read', 'rbac:groups:update')],
            loadComponent: () => import('./routes/admin/group-edit.component').then(m => m.AdminGroupEditComponent),
            title: 'Edit Group - SmartCapacity Control',
          },
          {
            path: 'permissions',
            canActivate: [requireAnyPermission('rbac:permissions:read')],
            loadComponent: () => import('./routes/admin/permissions-list.component').then(m => m.AdminPermissionsListComponent),
            title: 'Permissions - SmartCapacity Control',
          },
          {
            path: 'authentication',
            canActivate: [requirePermission('settings:auth:read')],
            component: AuthSettingsComponent,
            title: 'Authentication Settings - SmartCapacity Control',
          },
          {
            path: 'email',
            canActivate: [requireAnyPermission('settings:smtp:read', 'settings:smtp:update')],
            loadComponent: () => import('@zag/email/ui').then(m => m.SmtpSettingsComponent),
            title: 'Email Settings - SmartCapacity Control',
          },
          {
            path: 'ai',
            canActivate: [requireAnyPermission('settings:ai:read', 'settings:ai:update')],
            loadComponent: () => import('@zag/ai/ui').then(m => m.AiSettingsComponent),
            title: 'AI Settings - SmartCapacity Control',
          },
        ],
      },

      // ─── Cross-environment licensing routes ─────────────────────────
      {
        path: 'licensing',
        canActivate: [requireAnyPermission('licensing:definitions:read', 'licensing:definitions:manage')],
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'licences',
          },
          {
            path: 'licences',
            canActivate: [requireAnyPermission('licensing:definitions:read', 'licensing:definitions:manage')],
            loadComponent: () =>
              import('./routes/licensing/licences-collection-page.component').then(m => m.LicencesCollectionPageComponent),
            title: 'Licences - SmartCapacity Control',
          },
          {
            path: 'definitions',
            children: [
              {
                path: '',
                pathMatch: 'full',
                canActivate: [requireAnyPermission('licensing:definitions:read', 'licensing:definitions:manage')],
                loadComponent: () =>
                  import('./routes/licensing/licence-definitions-list-page.component').then(m => m.LicenceDefinitionsListPageComponent),
                title: 'Licence Definitions - SmartCapacity Control',
              },
              {
                path: 'new',
                canActivate: [requireAnyPermission('licensing:definitions:manage')],
                loadComponent: () =>
                  import('./routes/licensing/licence-definition-edit-page.component').then(m => m.LicenceDefinitionEditPageComponent),
                title: 'New Licence Definition - SmartCapacity Control',
              },
              {
                path: ':id',
                canActivate: [requireAnyPermission('licensing:definitions:read', 'licensing:definitions:manage')],
                loadComponent: () =>
                  import('./routes/licensing/licence-definition-edit-page.component').then(m => m.LicenceDefinitionEditPageComponent),
                title: 'Edit Licence Definition - SmartCapacity Control',
              },
            ],
          },
        ],
      },

      // ─── Cross-environment feature-flag routes ──────────────────────
      {
        path: 'feature-flags',
        canActivate: [requireAnyPermission('feature-flags:definitions:read', 'feature-flags:definitions:manage')],
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'definitions',
          },
          {
            path: 'definitions',
            children: [
              {
                path: '',
                pathMatch: 'full',
                canActivate: [requireAnyPermission('feature-flags:definitions:read', 'feature-flags:definitions:manage')],
                loadComponent: () =>
                  import('./routes/feature-flags/flag-definitions-list-page.component').then(m => m.FlagDefinitionsListPageComponent),
                title: 'Feature Flag Definitions - SmartCapacity Control',
              },
              {
                path: 'new',
                canActivate: [requireAnyPermission('feature-flags:definitions:manage')],
                loadComponent: () =>
                  import('./routes/feature-flags/flag-definition-edit-page.component').then(m => m.FlagDefinitionEditPageComponent),
                title: 'New Feature Flag - SmartCapacity Control',
              },
              {
                path: ':key',
                canActivate: [requireAnyPermission('feature-flags:definitions:read', 'feature-flags:definitions:manage')],
                loadComponent: () =>
                  import('./routes/feature-flags/flag-definition-edit-page.component').then(m => m.FlagDefinitionEditPageComponent),
                title: 'Edit Feature Flag - SmartCapacity Control',
              },
            ],
          },
        ],
      },

      // Legacy redirects for old routes
      {
        path: 'tenants',
        redirectTo: '/select-environment',
        pathMatch: 'full',
      },
      {
        path: 'environments',
        redirectTo: '/select-environment',
        pathMatch: 'full',
      },
    ],
  },
];
